import React from 'react';
import { useQuery } from 'react-query';

import { usePublicationContext } from 'components/Publication/PublicationContext';
import { useCommunityViewContext } from 'components/View/CommunityViewContext';
import { useWidgetDataFetcher } from 'hooks/useWidgetDataFetcher';
import { getAreaUnitLabel } from 'utils/helpers/units';

import CardAverageContent from '../Card/CardAverageContent';
import CardLabelContent from '../Card/CardLabelContent';
import CardPercentageContent from '../Card/CardPercentageContent';
import CardRatingContent from '../Card/CardRatingContent';
import { WIDGET_TYPES } from '../constants';
import { calculateValueDifference, shouldDisplayValueDifference } from '../valueDifferenceUtils';
import NoDataInfo from '../Widget/NoDataContent';
import { useWidgetContext } from '../Widget/WidgetContext';

const CardWidgetContent = () => {
  const { communityId, competitorId, community } = useCommunityViewContext();
  const { type, filters, rangeType, priceDisplay } = useWidgetContext();
  const { fetchWidgetData, showContractLabelAnnotations, snapshotUuids, communityContractIds } = useWidgetDataFetcher();
  const { publication } = usePublicationContext();
  const {
    competitors,
    careTypes,
    roomTypes,
    roomTypesPriceDisplay,
    careLevels,
    medicationLevels,
    reviewSources,
    reviewCategories,
    swotCategories,
    careLevelsStatsType,
    medicationLevelsStatsType,
    communityContractType,
    communityContractSelectionType,
    contractTypes,
  } = filters;

  const isRatingWidget = type === WIDGET_TYPES.RATING;

  const { data } = useQuery({
    queryKey: [type, communityId, filters, rangeType, priceDisplay, showContractLabelAnnotations, communityContractIds],
    queryFn: ({ signal }) =>
      fetchWidgetData(
        communityId,
        type,
        rangeType,
        {
          filters: {
            competitors,
            care_types: careTypes,
            room_types: roomTypes,
            room_types_price_display: roomTypesPriceDisplay,
            care_levels: careLevels,
            medication_levels: medicationLevels,
            review_sources: reviewSources,
            review_categories: reviewCategories,
            swot_categories: swotCategories,
            care_levels_stats_type: careLevelsStatsType,
            medication_levels_stats_type: medicationLevelsStatsType,
            community_contract_type: communityContractType,
            community_contract_selection_type: communityContractSelectionType,
            contract_types: contractTypes,
          },
          price_display: priceDisplay,
          ...(publication?.hash ? { public_hash: publication.hash } : {}),
        },
        { signal }
      ),
    refetchOnWindowFocus: false,
    enabled: communityId > 0 && (snapshotUuids !== null || isRatingWidget),
  });

  const cardData = data?.length > 0 ? data[0] : undefined;

  if (cardData && !cardData.community && !cardData.market) {
    return <NoDataInfo>No Data Available</NoDataInfo>;
  }

  const primaryValue = competitorId ? cardData?.market : cardData?.community;
  const secondaryValue = competitorId ? cardData?.community : cardData?.market;
  const footerLabel = competitorId ? 'My community avg.' : 'Competitor avg.';
  const comparisonTypeEnabled = filters?.comparisonTypeEnabled;
  const valueDisplay = filters?.valueDisplay;
  const shouldShowFooterDifference =
    footerLabel === 'Competitor avg.' &&
    shouldDisplayValueDifference(comparisonTypeEnabled, valueDisplay) &&
    typeof cardData?.community === 'number' &&
    typeof cardData?.market === 'number';
  const footerDifference = shouldShowFooterDifference
    ? calculateValueDifference(cardData?.community ?? null, cardData?.market ?? null, valueDisplay, type)
    : null;

  if (type === WIDGET_TYPES.OCCUPANCY) {
    return (
      <CardPercentageContent
        value={primaryValue}
        marketValue={secondaryValue}
        footerLabel={footerLabel}
        footerDifference={footerDifference}
        valueDisplay={valueDisplay}
      />
    );
  } else if (type === WIDGET_TYPES.RATING) {
    return (
      <CardRatingContent
        value={primaryValue}
        total={cardData?.total}
        marketValue={secondaryValue}
        footerLabel={footerLabel}
        showTotals={!competitorId}
        footerDifference={footerDifference}
        valueDisplay={valueDisplay}
      />
    );
  } else if (type === WIDGET_TYPES.SQUARE_FOOTAGE) {
    return (
      <CardLabelContent
        value={primaryValue}
        marketValue={secondaryValue}
        label={getAreaUnitLabel(community)}
        footerLabel={footerLabel}
        footerDifference={footerDifference}
        valueDisplay={valueDisplay}
      />
    );
  }

  return (
    <CardAverageContent
      value={primaryValue}
      marketValue={secondaryValue}
      footerLabel={footerLabel}
      footerDifference={footerDifference}
      valueDisplay={valueDisplay}
    />
  );
};

export default CardWidgetContent;
